"use client";

import { motion } from "framer-motion";
import { BackLink } from "@/components/back-link";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { delayChildren: 0, staggerChildren: 0.15 },
  },
};

const sectionVariants = {
  hidden: { opacity: 0, scale: 0.99, y: 5, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

// --- Status icons matching bazza.dev exactly ---
function InProgressStatusIcon() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 14 14" fill="none">
      <circle
        cx="7"
        cy="7"
        r="6"
        fill="none"
        stroke="lch(80% 90 85)"
        strokeWidth="2"
        strokeDasharray="3.14 0"
        strokeDashoffset="-0.7"
      />
      <circle
        cx="7"
        cy="7"
        r="2"
        fill="none"
        stroke="lch(80% 90 85)"
        strokeWidth="4"
        strokeDasharray="6.2517693806436885 100"
        strokeDashoffset="0"
        transform="rotate(-90 7 7)"
      />
    </svg>
  );
}

function DoneStatusIcon() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 14 14" fill="none">
      <circle
        cx="7"
        cy="7"
        r="6"
        fill="none"
        stroke="lch(48% 59.31 288.43)"
        strokeWidth="2"
        strokeDasharray="3.14 0"
        strokeDashoffset="-0.7"
      />
      <circle
        cx="7"
        cy="7"
        r="3"
        fill="none"
        stroke="lch(48% 59.31 288.43)"
        strokeWidth="6"
        strokeDasharray="18.84955592153876 100"
        strokeDashoffset="0"
        transform="rotate(-90 7 7)"
      />
      <path
        d="M10.951 4.24896C11.283 4.58091 11.283 5.11909 10.951 5.45104L5.95104 10.451C5.61909 10.783 5.0809 10.783 4.74896 10.451L2.74896 8.45104C2.41701 8.11909 2.41701 7.5809 2.74896 7.24896C3.0809 6.91701 3.61909 6.91701 3.95104 7.24896L5.35 8.64792L9.74896 4.24896C10.0809 3.91701 10.6191 3.91701 10.951 4.24896Z"
        fill="var(--sand-1)"
        strokeWidth="2"
      />
    </svg>
  );
}

type Project = {
  year: string;
  title: string;
  url: string;
  link: string;
  status: "in-progress" | "done";
};

const projects: Project[] = [
  {
    year: "2026",
    title: "Matteo LEDEE",
    url: "portfolio-v2-five-gilt.vercel.app",
    link: "#",
    status: "in-progress",
  },
  {
    year: "2026",
    title: "Sprout AI Agent",
    url: "github.com",
    link: "#",
    status: "in-progress",
  },
  {
    year: "2026",
    title: "Mntal",
    url: "mntal.app",
    link: "#",
    status: "in-progress",
  },
  {
    year: "2026",
    title: "Queue Dashboard",
    url: "github.com",
    link: "#",
    status: "in-progress",
  },
  {
    year: "2024",
    title: "rust-cpu-monitor",
    url: "github.com",
    link: "https://github.com/matteoLED/rust-cpu-monitor",
    status: "done",
  },
  {
    year: "2024",
    title: "go-cpu-monitor",
    url: "github.com",
    link: "https://github.com/matteoLED/go-cpu-monitor",
    status: "done",
  },
  {
    year: "2023",
    title: "game-dice",
    url: "netlify.app",
    link: "https://developpement--superb-gumption-bbd669.netlify.app/",
    status: "done",
  },
  {
    year: "2023",
    title: "quiz-js",
    url: "github.io",
    link: "https://matteoled.github.io/quiz-js/",
    status: "done",
  },
];

export default function ProjectsPage() {
  return (
    <div className="mx-auto w-full max-w-xl">
      <motion.div
        className="mt-16 flex flex-col gap-8 select-none"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="flex flex-col gap-4" variants={sectionVariants}>
          <BackLink href="/" label="[Index]" className="mb-2" />
          <h1 className="text-3xl tracking-tight">Projects</h1>
          <p className="font-medium text-[var(--sand-10)]">
            Side projects and{" "}
            <strong className="text-[var(--sand-12)] font-semibold">
              experiments
            </strong>
            &mdash; built to{" "}
            <strong className="text-[var(--sand-12)] font-semibold">
              learn
            </strong>
            , to{" "}
            <strong className="text-[var(--sand-12)] font-semibold">
              ship
            </strong>
            , and to solve{" "}
            <strong className="text-[var(--sand-12)] font-semibold">
              real problems
            </strong>
            .
          </p>
          <div className="h-px w-full bg-[var(--sand-6)] mt-4" />
        </motion.div>

        <motion.div className="flex flex-col" variants={sectionVariants}>
          {projects.map(({ year, title, url, link, status }) => (
            <a
              key={title}
              href={link}
              target={link === "#" ? undefined : "_blank"}
              rel={link === "#" ? undefined : "noopener noreferrer"}
              className="grid grid-cols-[max-content_minmax(0,1fr)] sm:grid-cols-[max-content_max-content_max-content_minmax(0,1fr)] gap-x-4 sm:gap-x-6 items-center py-2 group hover:bg-[var(--sand-2)] border border-transparent hover:border-[var(--sand-3)] px-2 sm:px-4 rounded-md transition-all duration-200"
            >
              <span className="font-medium text-[var(--sand-9)] hidden sm:block">{year}</span>
              <span className="flex items-center gap-3 text-base">
                {status === "in-progress" ? (
                  <InProgressStatusIcon />
                ) : (
                  <DoneStatusIcon />
                )}
                <span className="font-medium text-[var(--sand-12)]">
                  {title}
                </span>
              </span>
              <span className="font-medium text-[var(--sand-11)] hidden sm:block">
                {url}
              </span>
              <span />
            </a>
          ))}
        </motion.div>

        {/* Legend - dev only */}
        {process.env.NODE_ENV === "development" && (
          <motion.div
            className="flex flex-col gap-2 mt-4 text-sm px-4"
            variants={sectionVariants}
          >
            <span className="text-[var(--sand-8)] font-bold text-xs">Legend</span>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-base">
                <InProgressStatusIcon />
                <span className="text-[var(--sand-10)] text-sm">Building</span>
              </div>
              <div className="flex items-center gap-2 text-base">
                <DoneStatusIcon />
                <span className="text-[var(--sand-10)] text-sm">Done</span>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
